import videojs, { VideoJsPlayer } from 'video.js';

import { Event } from '@src/types/event';

import BaseNormalizer from './BaseNormalizer';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Options {}

export const PLUGIN_NAME = 'eventManagerInteractionNormalizer';

class InteractionNormalizer extends BaseNormalizer {
  vjsPlayer: VideoJsPlayer;

  constructor(player: VideoJsPlayer, options?: Options) {
    super(player, options);
    this.vjsPlayer = player;

    this.setupPlayOrPauseClickListeners();
    this.setupUserVolumeChangeListeners();
    this.setupGenericClickListeners();
  }

  private getTech() {
    return this.vjsPlayer.tech('i-know-wht-i-am-doing');
  }

  private setupPlayOrPauseClickListeners() {
    this.vjsPlayer.on(
      [
        Event.X_CLICK_POSTER,
        Event.X_CLICK_BIGPLAYBTN,
        Event.X_CLICK_PLAYTOGGLE,
      ],
      this.handleCLickToPlayOrPause
    );

    this.vjsPlayer.ready(() => {
      const isControlsEnabled = this.vjsPlayer.controls();

      if (isControlsEnabled) {
        this.vjsPlayer.controls(false); // deregister control handlers first to prioritize our custom handlers
      }

      // no need touchend listener as touching the tech on touch device wont trigger play/pause
      this.vjsPlayer.on(this.getTech(), 'click', this.handleCLickToPlayOrPause);

      if (isControlsEnabled) {
        this.vjsPlayer.controls(true); //register control handlers again
      }
    });
  }

  private handleCLickToPlayOrPause = () => {
    const eventType = this.vjsPlayer.paused()
      ? Event.CLICKTOPLAY
      : Event.CLICKTOPAUSE;

    if (eventType === Event.CLICKTOPLAY) {
      this.vjsPlayer.contextPlugin.addClickToPlayCount();
    } else if (eventType === Event.CLICKTOPAUSE) {
      this.vjsPlayer.contextPlugin.addClickToPauseCount();
    }
    this.vjsPlayer.trigger(eventType);
  };

  private disposeClickOrPauseClickListeners() {
    this.vjsPlayer.off(
      [
        Event.X_CLICK_POSTER,
        Event.X_CLICK_BIGPLAYBTN,
        Event.X_CLICK_PLAYTOGGLE,
      ],
      this.handleCLickToPlayOrPause
    );

    this.vjsPlayer.on('dispose', () => {
      this.vjsPlayer.off(
        this.getTech(),
        'click',
        this.handleCLickToPlayOrPause
      );
    });
  }

  private setupUserVolumeChangeListeners() {
    this.vjsPlayer.on(
      [Event.VOLUMEBARSLIDERINACTIVE, Event.MUTE, Event.UNMUTE],
      this.handleUserVolumeChange
    );
  }

  private handleUserVolumeChange = () => {
    this.vjsPlayer.contextPlugin.addUserVolumeChangeCount();
    this.vjsPlayer.trigger(Event.X_CLICK_USERVOLUMECHANGE);
  };

  private disposeUserVolumeChangeListeners() {
    this.vjsPlayer.off(
      [Event.VOLUMEBARSLIDERINACTIVE, Event.MUTE, Event.UNMUTE],
      this.handleUserVolumeChange
    );
  }

  private setupGenericClickListeners() {
    this.vjsPlayer.on(
      [Event.FULLSCREEN, Event.INLINESCREEN, Event.UNMUTE, Event.MUTE],
      this.handleGenericClick
    );
  }

  private disposeGenericClickListeners() {
    this.vjsPlayer.off(
      [Event.FULLSCREEN, Event.INLINESCREEN, Event.UNMUTE, Event.MUTE],
      this.handleGenericClick
    );
  }

  private handleGenericClick = (evt: videojs.EventTarget.Event) => {
    this.trigger(Event.CLICK, {
      ...this.createCommonEventData(),
      button: evt.type,
    });
  };

  dispose() {
    this.disposeGenericClickListeners();
    this.disposeUserVolumeChangeListeners();
    this.disposeClickOrPauseClickListeners();
    super.dispose();
  }
}

videojs.registerPlugin(PLUGIN_NAME, InteractionNormalizer);

export type InteractionNormalizerFactoryFn = (
  options?: Options
) => InteractionNormalizer;

export default InteractionNormalizer;
